import { useCallback } from "react";

import { useCachedResource } from "src-next/shared/cache/useCachedResource";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import {
  whatsappEndpointService,
  type WhatsAppEndpointContactsResult,
} from "../services/whatsappEndpointService";

const EMPTY_CONTACTS: fhir5.ContactPoint[] = [];

export interface UseWhatsAppEndpointContactsResult {
  endpointId?: string;
  contacts: fhir5.ContactPoint[];
  isLoading: boolean;
  errorMessage: string | null;
  isSaving: boolean;
  saveReorderedContacts: (input: {
    endpointId: string;
    contacts: fhir5.ContactPoint[];
  }) => Promise<void>;
  refetch: () => void;
}

function mapErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Não foi possível carregar os dados do WhatsApp.";
}

export function useWhatsAppEndpointContacts(
  organizationId?: string,
  options?: {
    /**
     * A seção só monta o painel quando está aberta. `enabled` evita a busca
     * no header (botão atualizar) antes da primeira abertura.
     */
    enabled?: boolean;
  },
): UseWhatsAppEndpointContactsResult {
  const trimmedOrganizationId = organizationId?.trim() || "";
  const enabled = (options?.enabled ?? true) && Boolean(trimmedOrganizationId);
  /**
   * Chave por organização. Reabrir a seção lê esta memória;
   * o botão atualizar chama `refresh` e ignora o hit.
   */
  const cacheKey = trimmedOrganizationId
    ? `crm:whatsapp-contacts:${trimmedOrganizationId}`
    : null;

  const {
    data: loadedContactsResult,
    isLoading,
    error,
    refresh,
  } = useCachedResource<WhatsAppEndpointContactsResult>({
    cacheKey,
    enabled,
    fetcher: () =>
      whatsappEndpointService.listContactsByOrganizationId(trimmedOrganizationId),
  });

  const saveTask = useCallback(
    async (input: { endpointId: string; contacts: fhir5.ContactPoint[] }) => {
      await whatsappEndpointService.reorderContactsByEndpointId(input);
      return undefined;
    },
    [],
  );

  const {
    run: saveContacts,
    loading: isSaving,
  } = useAsyncTask<
    [
      {
        endpointId: string;
        contacts: fhir5.ContactPoint[];
      },
    ],
    void,
    string
  >({
    task: saveTask,
    mapError: mapErrorMessage,
  });

  const endpointId = loadedContactsResult?.endpointId;
  const contacts = loadedContactsResult?.contacts ?? EMPTY_CONTACTS;

  return {
    endpointId,
    contacts,
    isLoading,
    errorMessage: error ? mapErrorMessage(error) : null,
    isSaving,
    saveReorderedContacts: async (input) => {
      await saveContacts(input);
    },
    refetch: () => {
      void refresh();
    },
  };
}

